import { appAlert } from '@/lib/appAlert';
import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect, useRouter } from 'expo-router';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { getErrorMessage } from '@/lib/functions';
import { Button } from '@/components/ui/Button';
import { fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import { lineItemTotal } from '@/lib/offers';
import {
  deleteOfferTemplate,
  listOfferTemplates,
  renameOfferTemplate,
  MAX_OFFER_TEMPLATES,
  type OfferTemplate,
} from '@/lib/offerTemplates';

/** Σύντομη περίληψη του περιεχομένου ενός προτύπου, για εμφάνιση στη λίστα. */
function templateSummary(tpl: OfferTemplate, t: (key: string, opts?: Record<string, unknown>) => string): string {
  if (tpl.offer_kind === 'goods') {
    const n = tpl.line_items?.length ?? 0;
    const total = (tpl.line_items ?? []).reduce((s, it) => s + lineItemTotal(it), 0);
    return t('offersScreens.productCount', { count: n, total: total.toFixed(2) });
  }
  return tpl.cost != null ? `${tpl.cost.toFixed(2)}€` : t('offersScreens.noSetCost');
}

export default function OfferTemplatesScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);

  const [templates, setTemplates] = useState<OfferTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [renameTarget, setRenameTarget] = useState<OfferTemplate | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const list = await listOfferTemplates();
      setTemplates(list);
    } catch (e) {
      setError(getErrorMessage(e, t('offersScreens.loadFailed')));
    } finally {
      setLoading(false);
    }
  }, []);

  useFocusEffect(useCallback(() => { void load(); }, [load]));

  function openRename(tpl: OfferTemplate) {
    setRenameTarget(tpl);
    setRenameValue(tpl.name);
  }

  async function confirmRename() {
    if (!renameTarget) return;
    const name = renameValue.trim();
    if (!name) return;
    setRenaming(true);
    try {
      await renameOfferTemplate(renameTarget.id, name);
      setTemplates((prev) => prev.map((t) => (t.id === renameTarget.id ? { ...t, name } : t)));
      setRenameTarget(null);
    } catch (e) {
      appAlert(t('common.error'), getErrorMessage(e, t('offersScreens.renameFailed')));
    } finally {
      setRenaming(false);
    }
  }

  function confirmDelete(tpl: OfferTemplate) {
    appAlert(
      t('offersScreens.deleteTemplateTitle'),
      t('offersScreens.deleteTemplateMessage', { name: tpl.name }),
      [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('offersScreens.deleteAction'),
          style: 'destructive',
          onPress: async () => {
            setDeletingId(tpl.id);
            try {
              await deleteOfferTemplate(tpl.id);
              setTemplates((prev) => prev.filter((item) => item.id !== tpl.id));
            } catch (e) {
              appAlert(t('common.error'), getErrorMessage(e, t('offersScreens.deleteFailed')));
            } finally {
              setDeletingId(null);
            }
          },
        },
      ],
    );
  }

  return (
    <View style={styles.root}>
      <View style={[styles.header, { paddingTop: insets.top + spacing.lg }]}>
        <Pressable onPress={() => router.back()} hitSlop={10} accessibilityRole="button">
          <Ionicons name="arrow-back" size={24} color={colors.text} />
        </Pressable>
        <Text style={styles.title}>{t('offersScreens.templatesTitle')}</Text>
        <View style={{ width: 24 }} />
      </View>

      {loading ? (
        <View style={styles.center}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      ) : error ? (
        <View style={styles.center}>
          <Text style={styles.emptyText}>{error}</Text>
        </View>
      ) : templates.length === 0 ? (
        <View style={styles.center}>
          <Ionicons name="copy-outline" size={48} color={colors.border} />
          <Text style={styles.emptyTitle}>{t('offersScreens.noTemplatesTitle')}</Text>
          <Text style={styles.emptyText}>
            {t('offersScreens.noTemplatesBody')}
          </Text>
        </View>
      ) : (
        <ScrollView
          style={styles.list}
          contentContainerStyle={[styles.listContent, { paddingBottom: insets.bottom + spacing.xl }]}
          showsVerticalScrollIndicator={false}
        >
          <Text style={styles.countHint}>{t('offersScreens.templatesCountHint', { count: templates.length, max: MAX_OFFER_TEMPLATES })}</Text>

          {templates.map((tpl) => (
            <View key={tpl.id} style={styles.card}>
              <View style={styles.cardHeader}>
                <View style={styles.kindBadge}>
                  <Ionicons
                    name={tpl.offer_kind === 'goods' ? 'cube-outline' : 'construct-outline'}
                    size={12}
                    color={colors.primary}
                  />
                  <Text style={styles.kindBadgeText}>{tpl.offer_kind === 'goods' ? t('offersScreens.offerKindGoods') : t('offersScreens.offerKindService')}</Text>
                </View>
              </View>
              <Text style={styles.cardName} numberOfLines={1}>{tpl.name}</Text>
              <Text style={styles.cardTitle} numberOfLines={1}>{tpl.title}</Text>
              <Text style={styles.cardSummary}>{templateSummary(tpl, t)}</Text>

              <View style={styles.cardActions}>
                <Pressable
                  style={styles.actionBtn}
                  onPress={() => router.push({ pathname: '/(app)/offers/new', params: { templateId: tpl.id } })}
                  accessibilityRole="button"
                >
                  <Ionicons name="paper-plane-outline" size={15} color={colors.primary} />
                  <Text style={styles.actionBtnText}>{t('offersScreens.useAction')}</Text>
                </Pressable>
                <Pressable style={styles.actionBtn} onPress={() => openRename(tpl)} accessibilityRole="button">
                  <Ionicons name="create-outline" size={15} color={colors.text} />
                  <Text style={[styles.actionBtnText, { color: colors.text }]}>{t('offersScreens.renameAction')}</Text>
                </Pressable>
                <Pressable
                  style={styles.actionBtn}
                  onPress={() => confirmDelete(tpl)}
                  disabled={deletingId === tpl.id}
                  accessibilityRole="button"
                >
                  {deletingId === tpl.id ? (
                    <ActivityIndicator size="small" color={colors.danger} />
                  ) : (
                    <>
                      <Ionicons name="trash-outline" size={15} color={colors.danger} />
                      <Text style={[styles.actionBtnText, { color: colors.danger }]}>{t('offersScreens.deleteAction')}</Text>
                    </>
                  )}
                </Pressable>
              </View>
            </View>
          ))}
        </ScrollView>
      )}

      <Modal visible={!!renameTarget} transparent animationType="fade" onRequestClose={() => setRenameTarget(null)}>
        <KeyboardAvoidingView style={styles.modalOverlay} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>{t('offersScreens.renameTemplateTitle')}</Text>
            <TextInput
              style={styles.modalInput}
              value={renameValue}
              onChangeText={setRenameValue}
              placeholder={t('offersScreens.templateNameFieldPlaceholder')}
              placeholderTextColor={colors.textMuted}
              autoFocus
              maxLength={60}
            />
            <View style={styles.modalActions}>
              <View style={{ flex: 1 }}>
                <Button title={t('common.cancel')} variant="secondary" onPress={() => setRenameTarget(null)} />
              </View>
              <View style={{ flex: 1 }}>
                <Button title={t('common.save')} onPress={confirmRename} loading={renaming} disabled={!renameValue.trim()} />
              </View>
            </View>
          </View>
        </KeyboardAvoidingView>
      </Modal>
    </View>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    root: { flex: 1, backgroundColor: colors.background },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: spacing.lg,
      paddingBottom: spacing.md,
    },
    title: { color: colors.text, fontSize: fontSize.lg, fontWeight: '700' },
    center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: spacing.xl, gap: spacing.sm },
    emptyTitle: { color: colors.text, fontSize: fontSize.md, fontWeight: '700', marginTop: spacing.sm },
    emptyText: { color: colors.textMuted, fontSize: fontSize.sm, textAlign: 'center' },
    list: { flex: 1 },
    listContent: { paddingHorizontal: spacing.lg, gap: spacing.md },
    countHint: { color: colors.textMuted, fontSize: fontSize.xs, marginBottom: spacing.xs },
    card: {
      backgroundColor: colors.surface,
      borderRadius: radius.lg,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.md,
      gap: 2,
    },
    cardHeader: { flexDirection: 'row', justifyContent: 'flex-end' },
    kindBadge: {
      flexDirection: 'row', alignItems: 'center', gap: 4,
      backgroundColor: `${colors.primary}14`,
      borderRadius: radius.full,
      paddingHorizontal: spacing.sm,
      paddingVertical: 3,
    },
    kindBadgeText: { color: colors.primary, fontSize: 11, fontWeight: '700' },
    cardName: { color: colors.text, fontSize: fontSize.md, fontWeight: '700' },
    cardTitle: { color: colors.textMuted, fontSize: fontSize.sm },
    cardSummary: { color: colors.text, fontSize: fontSize.sm, fontWeight: '600', marginTop: 2 },
    cardActions: {
      flexDirection: 'row',
      gap: spacing.md,
      marginTop: spacing.sm,
      paddingTop: spacing.sm,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: colors.border,
    },
    actionBtn: { flexDirection: 'row', alignItems: 'center', gap: 4 },
    actionBtnText: { color: colors.primary, fontSize: fontSize.sm, fontWeight: '600' },
    modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', padding: spacing.lg },
    modalCard: { backgroundColor: colors.surface, borderRadius: radius.lg, padding: spacing.lg, gap: spacing.md },
    modalTitle: { color: colors.text, fontSize: fontSize.lg, fontWeight: '700' },
    modalInput: {
      height: 48,
      borderRadius: radius.md,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surfaceAlt,
      color: colors.text,
      paddingHorizontal: spacing.md,
      fontSize: fontSize.md,
    },
    modalActions: { flexDirection: 'row', gap: spacing.sm },
  });
}
